import { supabase } from "@/integrations/supabase/client";
import type { FunctionInvokeOptions } from "@supabase/supabase-js";

/**
 * Auth-safe wrapper around `supabase.functions.invoke()`.
 *
 * WHY THIS EXISTS
 * The auto-generated `src/integrations/supabase/client.ts` installs a custom
 * `fetch` that strips the `Authorization` header for new-format publishable
 * keys (`sb_publishable_...`). That is correct for PostgREST/database calls,
 * but it means Edge Function calls can lose the caller's user token, so
 * server-side `auth.getUser()` fails ("Missing authorization header").
 *
 * This helper fetches the current session and explicitly attaches
 * `Authorization: Bearer <access_token>` on every Edge Function call, so
 * functions can always identify the calling user.
 *
 * It returns the SAME `{ data, error }` shape as `supabase.functions.invoke`,
 * so it is a drop-in replacement at call sites.
 *
 * For genuinely anonymous / pre-auth Edge Functions (e.g. public password
 * reset, anonymous SCOPE assessment flow) pass `{ requireAuth: false }` so the
 * call proceeds without a user token instead of erroring.
 */
export interface InvokeFunctionOptions extends FunctionInvokeOptions {
  /**
   * When true (default), a valid user session is required. If there is no
   * active session the call is NOT made and a clear "not authenticated" error
   * is returned. Set to false for public/anonymous Edge Functions.
   */
  requireAuth?: boolean;
}

export async function invokeFunction<T = any>(
  functionName: string,
  options: InvokeFunctionOptions = {},
): Promise<{ data: T | null; error: Error | null }> {
  const { requireAuth = true, headers, ...rest } = options;

  const { data: sessionData } = await supabase.auth.getSession();
  let accessToken = sessionData?.session?.access_token ?? null;

  if (requireAuth && !accessToken) {
    return {
      data: null,
      error: new Error("Not authenticated. Please sign in again."),
    };
  }

  const call = (token: string | null) => {
    const mergedHeaders: Record<string, string> = { ...(headers as Record<string, string> | undefined) };
    if (token && !mergedHeaders.Authorization) {
      mergedHeaders.Authorization = `Bearer ${token}`;
    }
    return supabase.functions.invoke<T>(functionName, { ...rest, headers: mergedHeaders });
  };

  const result = await call(accessToken);

  // Stale-session recovery.
  // A locally cached access token can still be unexpired while its server-side
  // session has been revoked (long-lived kiosk tabs, sign-out elsewhere).
  // PostgREST/RLS keeps working (the JWT signature is valid) but Edge Functions
  // call `auth.getUser()`, which fails with "Session not found" → the function
  // throws Unauthorized. Refresh once and retry before surfacing an error.
  if (requireAuth && result.error && (await isAuthFailure(result.error))) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    accessToken = refreshed?.session?.access_token ?? null;
    if (refreshError || !accessToken) {
      // Dead session (revoked elsewhere / expired refresh token). Clear the
      // stale local session so the app shows the sign-in screen instead of
      // repeatedly failing behind a logged-in-looking UI.
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        /* ignore */
      }
      return {
        data: null,
        error: new Error("Your session has expired. Please sign in again."),
      };
    }
    return call(accessToken);
  }

  return result;
}

/** True when an Edge Function failure was caused by a rejected caller token. */
async function isAuthFailure(error: unknown): Promise<boolean> {
  const ctx = (error as any)?.context;
  if (!ctx || typeof ctx.text !== "function") return false;
  const status: number | undefined = ctx.status;
  if (status && status !== 400 && status !== 401 && status !== 403) return false;
  try {
    const body = await ctx.clone().text();
    return /unauthorized|missing authorization|session not found|jwt/i.test(body);
  } catch {
    return status === 401;
  }
}

